import axios, { AxiosError } from 'axios';
import config from '../config/config';

// Use the centralized configuration for the API URL
const baseURL = config.apiUrl;

const instance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const refreshToken = async () => {
  try {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!token || !refreshToken) {
      throw new Error('No token or refresh token available');
    }

    
    const response = await axios.post(`${baseURL}/auth/refresh-token`, 
      { refreshToken },  
      {
        headers: {
          Authorization: `Bearer ${token}`  
        }
      }
    );

    
    const { token: newToken, refreshToken: newRefreshToken } = response.data;
    
    if (!newToken || !newRefreshToken) {
      throw new Error('No tokens received from refresh endpoint');
    }

    localStorage.setItem('token', newToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    return newToken;
  } catch (error) {
    
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    window.location.href = '/login';
    throw error;
  }
};

// Add a request interceptor
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    /* removed console.log */
    return config;
  },
  (error) => {
    
    return Promise.reject(error);
  }
);

// Add a response interceptor
instance.interceptors.response.use(
  (response) => {
    /* removed console.log */
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      
      return Promise.reject(error);
    }

    // If the error is not 401 or it's a refresh token request that failed, reject
    if (
      error.response?.status !== 401 ||
      originalRequest.url === '/auth/refresh-token' ||
      (originalRequest as any)._retry
    ) {
      /* removed console.error */
      return Promise.reject(error);
    }

    if (!isRefreshing) {
      isRefreshing = true;
      

      try {
        const newToken = await refreshToken();
        
        
        isRefreshing = false;
        (originalRequest as any)._retry = true;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        processQueue(null, newToken);
        return instance(originalRequest);
      } catch (refreshError) {
        
        isRefreshing = false;
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    }

    // Add request to queue if refresh is in progress
    return new Promise((resolve, reject) => {
      failedQueue.push({
        resolve: (token) => {
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          resolve(instance(originalRequest));
        },
        reject: (err) => {
          reject(err);
        },
      });
    });
  }
);

export default instance;
